<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, ref, watch, computed } from 'vue';
import type { ComponentProps } from '../../../../types/helper.ts';
import type { Link } from '../../../../types/link.ts';
import ShopCartItem from '../ShopCartItem/ShopCartItem.vue';
import { useShopCartStore } from '../../../../store/headerState/shopCartState.ts';
import Button from '../../../Button1XP/Button1XP.vue';
import Loading from '../../../Loading/Loading.vue';
import { isDesktop, isWidescreen } from '../../../../utils/breakpoints.js';
import type { PriceItem as Item } from '../../../../types/priceItem.ts';
import { useStoreTranslations } from '../../../../store/translations/translations.ts';
import IndividualPrice from '../../../IndividualPrice/IndividualPrice.vue';

const shopCartStore = useShopCartStore();
const storeTranslations = useStoreTranslations();

type Props = {
  shopCartItems: Item[];
  shippingLink: Link & { variant?: ComponentProps<typeof Button>['variant'] };
  shopCartUrl: Link & { variant?: ComponentProps<typeof Button>['variant'] };
  shopCartCheckoutUrl: Link & { variant?: ComponentProps<typeof Button>['variant'] };
  selectOptions: string[];
  totalPrice: string;
  quantity: number;
  isCartUpdating: boolean;
  isItemLoading?: {
    id: string;
    isLoading: boolean;
  };
};

const props = defineProps<Props>();
const maxQuantity = 25;
const filteredSelectOptions = computed(() =>
  props.selectOptions.filter((opt) => Number(opt) <= maxQuantity),
);

const isShopCartOpen = ref(false);
const isFlashing = ref(shopCartStore.isShopCartFlashing);
const itemPriceNew = ref<number[]>([]);
const shouldUpdate = ref<boolean>(false);

const emit = defineEmits(['newQuantity', 'priceChange', 'removeItem', 'amountWarning']);

const toggleCartClick = () => {
  shopCartStore.setShopCartOpen(!isShopCartOpen.value);
};

const handleItemPriceChange = (itemPrice, index) => {
  itemPriceNew.value[index] = itemPrice;
  emit('priceChange', itemPrice, index);
};

const clickOutsideShopCart = (e) => {
  const clickedElement = e.target;
  const clickOnShopCartButton = clickedElement.closest('.ShopCart1XP__container');
  const clickInShopCartPopper = clickedElement.closest('.ShopCart1XP__ShopCartPopper__container');

  if (!(clickOnShopCartButton || clickInShopCartPopper)) {
    if (isShopCartOpen.value) {
      e.preventDefault();
      shopCartStore.setShopCartOpen(!isShopCartOpen.value);
    }
  }
};

watch(
  () => shopCartStore.isShopCartOpen,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      isShopCartOpen.value = newValue;
    }
  },
);

watch(
  () => shopCartStore.isShopCartFlashing,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (isDesktop || isWidescreen) {
        isFlashing.value = newValue;
        isShopCartOpen.value = newValue;
      }
    }
  },
);

watch(
  () => props.isCartUpdating,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      shouldUpdate.value = newValue;
    }
  },
);

onBeforeMount(() => {
  window.addEventListener('click', (e) => clickOutsideShopCart(e));
});
onBeforeUnmount(() => {
  window.removeEventListener('click', (e) => clickOutsideShopCart(e));
});
</script>

<template>
  <div
    ref="ShopCartPopperRef"
    :class="[$style.container, isShopCartOpen && $style.isOpen, isFlashing && $style.isFlashing]"
  >
    <section :class="$style.upper">
      <h2>
        {{
          isFlashing
            ? storeTranslations.translations?.['shopCartPopper.shopCartPopper.titleFlashing'] ||
              'Added items to the cart'
            : storeTranslations.translations?.['shopCartPopper.shopCartPopper.title']
        }}
        <span
          v-if="!isFlashing"
          :class="$style.quantity"
          :title="storeTranslations.translations?.['shopCartPopper.shopCart.quantity']"
        >
          {{ quantity || 0 }}
        </span>
      </h2>
      <button
        v-if="!isFlashing"
        :class="$style.closeButton"
        :title="storeTranslations.translations?.['shopCartPopper.closeButton.title']"
        @click="toggleCartClick"
      >
        <span class="accessible-link-text">
          {{ storeTranslations.translations?.['shopCartPopper.closeButton.label'] }}
        </span>
      </button>
    </section>
    <section :class="$style.middle">
      <div v-if="shouldUpdate" :class="$style.isLoading">
        <Loading />
      </div>
      <div
        :class="$style.shopCartItems"
        role="list"
        :title="storeTranslations.translations?.['shopCartPopper.shopCartPopper.itemList']"
      >
        <ShopCartItem
          v-for="(item, index) in shopCartItems"
          :key="item.id + index"
          :is-loading="isItemLoading?.id === item.id && isItemLoading?.isLoading"
          :is-read-only="isFlashing"
          :item="item"
          :select-options="filteredSelectOptions"
          :value="itemPriceNew[index]"
          @amount-warning="(warning) => emit('amountWarning', warning)"
          @item-quantity-change="({ quantity, item }) => emit('newQuantity', { quantity, item })"
          @remove-item="(item) => emit('removeItem', item, index)"
          @updated-price="(itemPrice) => handleItemPriceChange(itemPrice, index)"
        />
      </div>
    </section>
    <section v-if="isFlashing" :class="$style.lower">
      <div :class="$style.totalPrice">
        <h3>
          {{
            storeTranslations.translations?.['shopCartPopper.shopCart.quantity'] +
            ' : ' +
            (shopCartItems[0]?.quantity ?? '')
          }}
        </h3>
        <div :class="$style.priceBox">
          <h3>
            <IndividualPrice
              :btype-list="[
                {
                  btype: shopCartItems[0]?.productType,
                  minimalPrice: shopCartItems[0]?.pricesingle,
                },
              ]"
              :product-type="shopCartItems[0]?.productType"
              :quantity="Number(shopCartItems[0]?.quantity)"
              :value="Number(shopCartItems[0]?.pricesingle)"
            />
          </h3>
          <span>
            {{ storeTranslations.translations?.['shopCartPopper.shopCartPopper.disclaimerLabel'] }}
            <a
              v-if="shippingLink"
              :href="shippingLink.url"
              :target="shippingLink.newWindow ? '_blank' : '_self'"
            >
              {{ shippingLink.label }}
            </a>
          </span>
        </div>
      </div>
      <div :class="$style.actions">
        <Button
          v-if="shopCartUrl"
          size="s"
          start-icon="shopping_cart"
          :text="shopCartUrl?.label"
          :url="shopCartUrl.url"
          variant="primary"
        />
      </div>
    </section>
    <section v-else :class="$style.lower">
      <div :class="$style.totalPrice">
        <h3>{{ storeTranslations.translations?.['shopCartPopper.shopCartPopper.totalLabel'] }}</h3>
        <div :class="$style.priceBox">
          <h3>{{ totalPrice }}</h3>
          <span>
            {{ storeTranslations.translations?.['shopCartPopper.shopCartPopper.disclaimerLabel'] }}
            <a
              v-if="shippingLink"
              :href="shippingLink.url"
              :target="shippingLink.newWindow ? '_blank' : '_self'"
            >
              {{ shippingLink.label }}
            </a>
          </span>
        </div>
      </div>
      <div :class="$style.actions">
        <Button
          v-if="shopCartCheckoutUrl"
          size="s"
          start-icon="shopping_cart"
          :text="shopCartCheckoutUrl?.label"
          :url="shopCartCheckoutUrl.url"
          variant="primary"
        />
        <Button
          v-if="shopCartUrl"
          size="s"
          :text="shopCartUrl?.label"
          :url="shopCartUrl.url"
          variant="secondary"
        />
      </div>
    </section>
  </div>
</template>

<style lang="scss" module>
@forward './ShopCartPopper.scss';
</style>
